<template>
  <section id="explore" class="signoff">
    <div class="container signoff__inner">
      <!-- A soft accent wash behind the mark. Static, so it costs one paint and
           nothing per frame, and it sits under the honeycomb the cursor
           reveals rather than competing with it. -->
      <span class="signoff__halo" aria-hidden="true"/>

      <!-- The rule gives this a width and leaves the height to the picture,
           which is nothing at all until the file lands and the name below it
           steps down. These say the shape before the bytes do. -->
      <img
        src="/android-chrome-512x512.png"
        :alt="`${personalInfo.fullName} logo`"
        class="signoff__logo"
        width="512"
        height="512"
        decoding="async"
      >

      <h2 class="signoff__name">{{ personalInfo.name }}</h2>
      <p class="signoff__line">{{ closing }}</p>

      <!-- The end of the track is where somebody has just spent a few minutes
           on the work and is deciding whether that was the end of it. Asking
           here costs a visitor nothing and asks at the only moment they have
           the whole picture. -->
      <div class="signoff__signup">
        <NewsletterSignup source="sign-off" compact />
      </div>

      <ul class="signoff__social">
        <li v-for="link in social" :key="link.name">
          <a :href="link.url" target="_blank" rel="noopener noreferrer" class="signoff__link">
            <svg v-if="link.icon === 'x'" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24H16.2l-5.21-6.82-5.97 6.82H1.7l7.73-8.83L1.28 2.25h6.93l4.71 6.23zm-1.16 17.52h1.83L7.08 4.13H5.11z" />
            </svg>
            <svg v-else-if="link.icon === 'instagram'" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <line x1="17.5" y1="6.5" x2="17.5" y2="6.5" />
            </svg>
            <svg v-else-if="link.icon === 'github'" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
            </svg>
            <svg v-else-if="link.icon === 'whatsapp'" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.39a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 1.67c2.2 0 4.27.86 5.83 2.42a8.2 8.2 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.25 8.24a8.2 8.2 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.55 3.7-8.24 8.25-8.24z"/>
              <path d="M9.32 7.05c-.17-.38-.34-.38-.5-.39h-.42c-.15 0-.39.05-.6.28-.2.23-.78.76-.78 1.85s.8 2.15.91 2.3c.11.14 1.55 2.48 3.82 3.38 1.88.74 2.27.6 2.68.56.4-.04 1.31-.54 1.5-1.05.18-.52.18-.96.13-1.05-.06-.09-.21-.14-.44-.26-.23-.11-1.31-.65-1.51-.72-.2-.08-.35-.11-.5.11-.14.23-.57.72-.7.87-.13.14-.26.16-.48.05-.23-.11-.96-.35-1.82-1.12-.68-.6-1.13-1.34-1.26-1.57-.14-.23-.02-.35.1-.47.1-.1.22-.26.34-.4.11-.13.15-.23.23-.38.07-.15.04-.29-.02-.4-.06-.12-.5-1.22-.68-1.67z"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9" />
              <ellipse cx="12" cy="12" rx="4" ry="9" />
              <line x1="3" y1="12" x2="21" y2="12" />
            </svg>
            <span>{{ link.name }}</span>
          </a>
        </li>
      </ul>

      <!-- The track ends here, so this closes the loop rather than leaving
           somebody to scroll all the way back by hand. -->
      <button type="button" class="signoff__top" @click="backToStart">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="11 18 5 12 11 6" />
        </svg>
        <span>Back to the start</span>
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { personalInfo } from '~/data/content';

const closing = "That's the whole thing. Say hello, or go back and look again.";

// Only accounts with a link. The Instagram entry sits empty until a handle
// exists, and an anchor to nowhere is worse than one fewer icon.
const social = computed(() => personalInfo.socialLinks.filter((link) => link.url));

const scrollState = useState('horizontalScroll');

function backToStart() {
  const hero = document.getElementById('hero');
  if (hero && scrollState.value?.scrollToPanel) {
    scrollState.value.scrollToPanel(hero);
    return;
  }
  // Mobile, where the track is stacked and there is nothing to translate.
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
</script>

<style lang="scss" scoped>
.signoff {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 4vh, 3rem) 0;
  background: transparent;

  // Unpinned below this, so the padding is the only gap between sections. The
  // last panel also wants room under it rather than ending on the fold.
  @media (max-width: 768px) {
    padding: clamp(3rem, 9vh, 4.5rem) 0 clamp(4rem, 12vh, 6rem);
  }
}

.signoff__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: clamp(0.35rem, 1.2vh, 0.8rem);
}

.signoff__halo {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(560px, 80vw);
  aspect-ratio: 1;
  transform: translate(-50%, -62%);
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(var(--accent-rgb), 0.16) 0%,
    rgba(var(--accent-rgb), 0.06) 38%,
    rgba(var(--accent-rgb), 0) 70%
  );
  pointer-events: none;
  z-index: 0;
}

.signoff__logo {
  position: relative;
  z-index: 1;
  width: clamp(96px, 11vw + 6vh, 180px);
  height: auto;
  // White line-art on transparency. Flattened to black here so it reads on the
  // light theme, and left alone in dark, exactly as the hero mark is handled.
  filter: brightness(0);
  opacity: 0.88;
}

.signoff__name {
  position: relative;
  z-index: 1;
  margin: clamp(0.4rem, 1.4vh, 0.9rem) 0 0;
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(1.05rem, 1.3vw + 1vh, 1.8rem);
  font-weight: 500;
  letter-spacing: 0.34em;
  // The tracking above adds a gap after the final letter too, which drags the
  // line off centre without this.
  text-indent: 0.34em;
  text-transform: uppercase;
  color: var(--text);
}

.signoff__line {
  position: relative;
  z-index: 1;
  max-width: 34ch;
  margin: 0;
  font-size: clamp(0.78rem, 0.4vw + 0.6vh, 0.92rem);
  line-height: 1.65;
  color: var(--muted);
}

.signoff__signup {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  width: min(360px, 100%);
  margin-top: clamp(0.6rem, 1.8vh, 1.1rem);
}

.signoff__social {
  position: relative;
  z-index: 1;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.45rem;
  margin: clamp(0.6rem, 2vh, 1.2rem) 0 0;
  padding: 0;
}

.signoff__link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.42rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(var(--border-rgb), 0.45);
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--secondary);
  text-decoration: none;
  transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out, background 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    color: var(--accent);
    border-color: rgba(var(--accent-rgb), 0.5);
    background: rgba(var(--accent-rgb), 0.06);
    text-decoration: none;
  }
}

.signoff__top {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: clamp(0.7rem, 2.2vh, 1.4rem);
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  cursor: pointer;
  transition: color 0.2s ease-in-out;

  svg { transition: transform 0.2s ease-in-out; }

  &:hover,
  &:focus-visible {
    color: var(--accent);
    svg { transform: translateX(-4px); }
  }
}

:root.dark .signoff__logo {
  filter: none;
  opacity: 0.92;
}

@media (prefers-reduced-motion: reduce) {
  .signoff__top svg,
  .signoff__link {
    transition: none;
  }
}
</style>
